/**
 * m3 echo Frontend - Custom Autopilot Tab
 *
 * Tools tab for managing custom autopilots.
 * Tests whether prompt timing affects LLM results.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import {
  customAutopilotApi,
  CustomAutopilotResponse,
  CustomAutopilotDetailResponse,
  CustomAutopilotChartDataPoint,
} from '../../services/admin';
import CreateCustomAutopilotDialog from './CreateCustomAutopilotDialog';
import { useUIStore } from '../../stores/useUIStore';

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-';
  // Ensure UTC dates are parsed correctly (add Z if not present)
  const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
  return new Date(utcDateString).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
};

const formatDuration = (hours: number): string => {
  if (hours < 24) return `${hours} Stunden`;
  const days = Math.floor(hours / 24);
  return `${days} Tag${days > 1 ? 'e' : ''}`;
};

const formatFrequency = (minutes: number): string => {
  if (minutes < 60) return `${minutes} Min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} Std`;
};

const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
  switch (status) {
    case 'pending':
      return 'default';
    case 'running':
      return 'primary';
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'cancelled':
      return 'warning';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'pending':
      return 'Ausstehend';
    case 'running':
      return 'Läuft';
    case 'completed':
      return 'Abgeschlossen';
    case 'failed':
      return 'Fehlgeschlagen';
    case 'cancelled':
      return 'Abgebrochen';
    default:
      return status;
  }
};

export const CustomAutopilotTab: React.FC = () => {
  // Get job detail dialog function from store
  const { openJobDetailDialog } = useUIStore();

  // List view state
  const [autopilots, setAutopilots] = useState<CustomAutopilotResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Detail view state
  const [selectedAutopilot, setSelectedAutopilot] = useState<CustomAutopilotDetailResponse | null>(null);
  const [chartData, setChartData] = useState<CustomAutopilotChartDataPoint[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [autopilotToDelete, setAutopilotToDelete] = useState<CustomAutopilotResponse | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Polling ref
  const pollingRef = React.useRef<NodeJS.Timeout | null>(null);

  const loadAutopilots = useCallback(async () => {
    setLoading(true);
    try {
      const response = await customAutopilotApi.list();
      setAutopilots(response.data.autopilots);
      setError(null);
    } catch (err: any) {
      console.error('Error loading autopilots:', err);
      setError(err.response?.data?.detail || 'Fehler beim Laden der Custom Autopilots');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAutopilotDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try {
      const [detailResponse, chartResponse] = await Promise.all([
        customAutopilotApi.get(id),
        customAutopilotApi.getChartData(id),
      ]);
      setSelectedAutopilot(detailResponse.data);
      setChartData(chartResponse.data.data_points);
      setError(null);
    } catch (err: any) {
      console.error('Error loading autopilot detail:', err);
      setError(err.response?.data?.detail || 'Fehler beim Laden der Details');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadAutopilots();
  }, [loadAutopilots]);

  // Polling for running autopilots
  useEffect(() => {
    const hasRunning = autopilots.some(a => a.status === 'running' || a.status === 'pending');

    if (hasRunning) {
      pollingRef.current = setInterval(() => {
        loadAutopilots();
        if (selectedAutopilot && (selectedAutopilot.status === 'running' || selectedAutopilot.status === 'pending')) {
          loadAutopilotDetail(selectedAutopilot.id);
        }
      }, 30000); // Poll every 30 seconds
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [autopilots, selectedAutopilot, loadAutopilots, loadAutopilotDetail]);

  const handleViewDetail = (autopilot: CustomAutopilotResponse) => {
    loadAutopilotDetail(autopilot.id);
  };

  const handleBackToList = () => {
    setSelectedAutopilot(null);
    setChartData([]);
    loadAutopilots();
  };

  const handleCancel = async (id: number) => {
    setCancellingId(id);
    try {
      await customAutopilotApi.cancel(id);
      await loadAutopilots();
      if (selectedAutopilot?.id === id) {
        await loadAutopilotDetail(id);
      }
    } catch (err: any) {
      console.error('Error cancelling autopilot:', err);
      setError(err.response?.data?.detail || 'Fehler beim Abbrechen');
    } finally {
      setCancellingId(null);
    }
  };

  const handleDeleteClick = (autopilot: CustomAutopilotResponse) => {
    setAutopilotToDelete(autopilot);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!autopilotToDelete) return;

    setDeletingId(autopilotToDelete.id);
    try {
      await customAutopilotApi.delete(autopilotToDelete.id);
      setDeleteDialogOpen(false);
      setAutopilotToDelete(null);
      if (selectedAutopilot?.id === autopilotToDelete.id) {
        handleBackToList();
      } else {
        await loadAutopilots();
      }
    } catch (err: any) {
      console.error('Error deleting autopilot:', err);
      setError(err.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreateSuccess = () => {
    setCreateDialogOpen(false);
    loadAutopilots();
  };

  // Render detail view
  if (selectedAutopilot) {
    const progress = selectedAutopilot.total_expected_runs
      ? (selectedAutopilot.current_run_count / selectedAutopilot.total_expected_runs) * 100
      : 0;

    // Every run with a score counts, whatever its status (a failed run keeps
    // what it measured); a real 0 does, a run without a valid result (null)
    // does not.
    const scoredRuns = (selectedAutopilot.runs || []).filter(
      (r): r is typeof r & { average_score: number } => r.average_score !== null && r.average_score !== undefined
    );
    const avgScore = scoredRuns.length > 0
      ? scoredRuns.reduce((sum, r) => sum + r.average_score, 0) / scoredRuns.length
      : null;

    // Prepare chart data
    const chartDataFormatted = chartData.map(point => ({
      ...point,
      timestamp: new Date(point.timestamp).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }),
      score: point.average_score,
    }));

    return (
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBackToList}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6">
              {selectedAutopilot.name || `Custom Autopilot #${selectedAutopilot.id}`}
            </Typography>
            <Chip
              label={getStatusLabel(selectedAutopilot.status)}
              color={getStatusColor(selectedAutopilot.status)}
              size="small"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => loadAutopilotDetail(selectedAutopilot.id)}
              disabled={detailLoading}
            >
              Aktualisieren
            </Button>
            {(selectedAutopilot.status === 'pending' || selectedAutopilot.status === 'running') && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={cancellingId === selectedAutopilot.id ? <CircularProgress size={20} /> : <CancelIcon />}
                onClick={() => handleCancel(selectedAutopilot.id)}
                disabled={cancellingId === selectedAutopilot.id}
              >
                Abbrechen
              </Button>
            )}
            {selectedAutopilot.status !== 'running' && (
              <Button
                variant="outlined"
                color="error"
                startIcon={deletingId === selectedAutopilot.id ? <CircularProgress size={20} /> : <DeleteIcon />}
                onClick={() => handleDeleteClick(selectedAutopilot)}
                disabled={deletingId === selectedAutopilot.id}
              >
                Löschen
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {detailLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Fortschritt
                    </Typography>
                    <Typography variant="h4">
                      {selectedAutopilot.current_run_count} / {selectedAutopilot.total_expected_runs}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Durchschnitts-Score
                    </Typography>
                    <Typography variant="h4">
                      {avgScore !== null ? avgScore.toFixed(1) : '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      aus {scoredRuns.length} bewerteten Runs
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Frequenz
                    </Typography>
                    <Typography variant="h4">
                      {formatFrequency(selectedAutopilot.frequency_minutes)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Dauer: {formatDuration(selectedAutopilot.duration_hours)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Nächster Run
                    </Typography>
                    <Typography variant="h6">
                      {selectedAutopilot.next_run_at
                        ? formatDateTime(selectedAutopilot.next_run_at)
                        : '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ende: {formatDateTime(selectedAutopilot.end_at)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Score Chart */}
            {chartDataFormatted.length > 0 && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Score-Verlauf
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartDataFormatted}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="timestamp"
                      tick={{ fontSize: 12 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      domain={[0, 10]}
                      tick={{ fontSize: 12 }}
                    />
                    <RechartsTooltip
                      formatter={(value: number) => [value?.toFixed(2), 'Score']}
                    />
                    <ReferenceLine y={5} stroke="#ff9800" strokeDasharray="5 5" />
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="#1976d2"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            )}

            {/* Run History Table */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Run-Historie ({selectedAutopilot.runs?.length || 0} Runs)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Geplant</TableCell>
                      <TableCell>Ausgeführt</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Score</TableCell>
                      <TableCell>Job</TableCell>
                      <TableCell align="center">Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedAutopilot.runs?.map((run) => (
                      <TableRow key={run.id}>
                        <TableCell>{run.id}</TableCell>
                        <TableCell>
                          {formatDateTime(run.scheduled_at)}
                          {run.shifted_from && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              (verschoben von {formatDateTime(run.shifted_from)})
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{formatDateTime(run.executed_at)}</TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(run.status)}
                            color={getStatusColor(run.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {run.average_score !== null && run.average_score !== undefined
                            ? run.average_score.toFixed(2)
                            : run.status === 'completed' || run.status === 'failed' ? 'nicht auswertbar' : '-'}
                        </TableCell>
                        <TableCell>
                          {run.job_id ? `#${run.job_id}` : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {run.job_id && (
                            <Tooltip title="Job-Details anzeigen">
                              <IconButton
                                size="small"
                                onClick={() => openJobDetailDialog(run.job_id!)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!selectedAutopilot.runs || selectedAutopilot.runs.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          Noch keine Runs ausgeführt
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Config Info */}
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Konfiguration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Modelle:</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {selectedAutopilot.models.map((model) => (
                      <Chip key={model} label={model} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Prompts:</Typography>
                  <Typography variant="body2">
                    {selectedAutopilot.prompts.length} Prompt(s) konfiguriert
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Erstellt:</Typography>
                  <Typography variant="body2">{formatDateTime(selectedAutopilot.created_at)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Start:</Typography>
                  <Typography variant="body2">{formatDateTime(selectedAutopilot.start_at)}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </Box>
    );
  }

  // Render list view
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Custom Autopilots</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAutopilots}
            disabled={loading}
          >
            Aktualisieren
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Neuer Autopilot
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : autopilots.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Fortschritt</TableCell>
                <TableCell>Frequenz</TableCell>
                <TableCell>Dauer</TableCell>
                <TableCell>Erstellt</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {autopilots.map((autopilot) => (
                <TableRow key={autopilot.id} hover>
                  <TableCell>{autopilot.id}</TableCell>
                  <TableCell>
                    {autopilot.name || `Custom Autopilot #${autopilot.id}`}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(autopilot.status)}
                      color={getStatusColor(autopilot.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={autopilot.total_expected_runs
                          ? (autopilot.current_run_count / autopilot.total_expected_runs) * 100
                          : 0}
                        sx={{ width: 60 }}
                      />
                      <Typography variant="body2">
                        {autopilot.current_run_count}/{autopilot.total_expected_runs}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{formatFrequency(autopilot.frequency_minutes)}</TableCell>
                  <TableCell>{formatDuration(autopilot.duration_hours)}</TableCell>
                  <TableCell>{formatDateTime(autopilot.created_at)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title="Details anzeigen">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetail(autopilot)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {(autopilot.status === 'pending' || autopilot.status === 'running') && (
                        <Tooltip title="Abbrechen">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleCancel(autopilot.id)}
                            disabled={cancellingId === autopilot.id}
                          >
                            {cancellingId === autopilot.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <CancelIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      {autopilot.status !== 'running' && (
                        <Tooltip title="Löschen">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(autopilot)}
                            disabled={deletingId === autopilot.id}
                          >
                            {deletingId === autopilot.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">
          Noch keine Custom Autopilots erstellt. Klicken Sie auf "Neuer Autopilot", um einen zu erstellen.
        </Alert>
      )}

      {/* Create Dialog */}
      <CreateCustomAutopilotDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Custom Autopilot löschen?</DialogTitle>
        <DialogContent>
          <Typography>
            Sind Sie sicher, dass Sie den Custom Autopilot
            "{autopilotToDelete?.name || `#${autopilotToDelete?.id}`}" löschen möchten?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deletingId !== null}
          >
            {deletingId !== null ? <CircularProgress size={20} /> : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomAutopilotTab;
